/**
 * Brand Mappings tab (Jason, 29 Oct 2026), next to Category Mappings on both Item Staging pages.
 *
 * Every Vend brand seen on staged items, mapped to a Deeliver brand (the Brands list), to
 * "No brand" (the product's brand stays blank: fillers like Miscellaneous or Vaperite), or left
 * unmapped, which passes the Vend name through unchanged (nothing is held for it, unlike an
 * unmapped category). Saving, changing or removing a mapping also updates the brand's live
 * products straight away; a person's own brand edit on a product always wins.
 *
 * Exclude works like a type's: its pending new items are withdrawn to Excluded Products, new ones
 * arrive withdrawn, and the dialog can also hide its live products from the app. Excluding keeps
 * the brand's mapping. Include brings everything back.
 *
 * Creating a Deeliver brand here is not offered: a brand needs its greyscale logo for the app's
 * brand rail, which is done on the Brands page.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  Fade, FormControl, FormControlLabel, Grid, IconButton, InputAdornment, InputLabel, MenuItem, Pagination,
  Paper, Select, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip,
  Typography, alpha,
} from '@mui/material';
import {
  Search as SearchIcon,
  Delete as DeleteIcon,
  Close as CancelIcon,
  Save as SaveIcon,
  SelectAll as SelectAllIcon,
  LinkOff as UnmappedIcon,
  Link as MappedIcon,
  BrandingWatermark as BrandIcon,
  Block as ExcludeIcon,
  SettingsBackupRestore as IncludeIcon,
  HelpOutline as NoVendBrandIcon,
} from '@mui/icons-material';
import { useNotify } from 'react-admin';
import authInstance from '../../services/httpClient';
import { useAdminTheme } from '../../admin-theme';
import { useScrollToListTop } from '../../hooks/useScrollToListTop';
import { GlowCard } from './ui';
import { stagingApiBase } from './stagingOrg';
import { useDebounced } from '../Discounts/useDebounced';

interface VendBrand {
  name: string;
  /** Distinct SKUs staged with this brand. */
  itemCount: number;
  /** Live products of this brand (Sell in app ON, active). */
  liveCount: number;
  /** New items of this brand still in review (pending or flagged). */
  waitingCount: number;
  excluded: boolean;
  /** Excluded with "hide its live products". */
  hideLive: boolean;
  /** Mapped to No brand: the product's brand stays blank. */
  noBrand: boolean;
  mappedTo: { deeliverBrandId: string; deeliverBrandName: string } | null;
}

interface BrandStats {
  total: number;
  mapped: number;
  unmapped: number;
  excluded: number;
  itemsWithoutBrand: number;
}

interface DeeliverBrand { id: string; name: string; imageUrl: string | null }

/** The Select value for "No brand" (a Deeliver brand is its id). */
const NO_BRAND = '__no_brand__';
const PAGE_SIZE = 50;

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;
const targetBody = (value: string) => (value === NO_BRAND ? { noBrand: true } : { deeliverBrandId: value });
const isMapped = (b: VendBrand) => b.noBrand || b.mappedTo !== null;

export default function BrandMappingsTab({ onChanged }: { onChanged?: () => void }) {
  const { colors, elevatedInputStyle } = useAdminTheme();
  const notify = useNotify();
  const base = `${stagingApiBase()}/staging/brand-mappings`;

  const [brands, setBrands] = useState<VendBrand[]>([]);
  const [deeliverBrands, setDeeliverBrands] = useState<DeeliverBrand[]>([]);
  const [stats, setStats] = useState<BrandStats | null>(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState('');
  const [filterMode, setFilterMode] = useState<'all' | 'mapped' | 'unmapped' | 'excluded'>('all');
  const [page, setPage] = useState(1);
  const listTopRef = useScrollToListTop(page);
  // the request waits until typing pauses (300 ms, as the other admin search boxes)
  const q = useDebounced(search.trim(), 300);

  const [editing, setEditing] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkValue, setBulkValue] = useState('');
  // a selection belongs to the rows it was made on: drop it when the list changes
  useEffect(() => { setSelected(new Set()); }, [page, filterMode, q]);

  // Exclude on a brand with live products: confirm, with "also hide its live products" (off by default).
  const [confirmExclude, setConfirmExclude] = useState<VendBrand | null>(null);
  const [hideLive, setHideLive] = useState(false);

  const fetchBrands = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, unknown> = { page, limit: PAGE_SIZE, mapped: filterMode };
      if (q) params.search = q;
      const { data } = await authInstance.get(base, { params });
      setBrands(data.brands || []);
      setTotal(data.total || 0);
      if (data.stats) setStats(data.stats);
    } catch (err: any) {
      notify(`Failed to load brand mappings: ${err.response?.data?.error || err.message}`, { type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [base, page, filterMode, q, notify]);

  useEffect(() => { fetchBrands(); }, [fetchBrands]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await authInstance.get(`${base}/brands`);
        setDeeliverBrands(data.brands || []);
      } catch {
        // the dropdown retries on the next visit
      }
    })();
  }, [base]);

  const afterChange = () => { fetchBrands(); onChanged?.(); };

  const targetLabel = (value: string) => (value === NO_BRAND
    ? 'No brand'
    : deeliverBrands.find((b) => b.id === value)?.name ?? 'the Deeliver brand');

  const startEdit = (b: VendBrand) => {
    setEditing(b.name);
    setEditValue(b.noBrand ? NO_BRAND : b.mappedTo?.deeliverBrandId ?? '');
  };

  const handleSave = async (b: VendBrand) => {
    if (!editValue) return;
    setSaving(true);
    try {
      const { data } = await authInstance.put(`${base}/${encodeURIComponent(b.name)}`, targetBody(editValue));
      notify(
        `"${b.name}" mapped to ${targetLabel(editValue)}${data?.rewritten ? ` — ${plural(data.rewritten, 'live product')} updated` : ''}`,
        { type: 'success' },
      );
      setEditing(null);
      afterChange();
    } catch (err: any) {
      notify(`Failed to save the mapping: ${err.response?.data?.error || err.message}`, { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (b: VendBrand) => {
    try {
      const { data } = await authInstance.delete(`${base}/${encodeURIComponent(b.name)}`);
      notify(
        `Mapping for "${b.name}" removed${data?.rewritten ? ` — ${plural(data.rewritten, 'live product')} back to the Vend name` : ''}`,
        { type: 'success' },
      );
      afterChange();
    } catch (err: any) {
      notify(`Failed to remove the mapping: ${err.response?.data?.error || err.message}`, { type: 'error' });
    }
  };

  const handleSetExcluded = async (b: VendBrand, excluded: boolean, hideLiveProducts = false) => {
    try {
      const { data } = await authInstance.put(
        `${base}/${encodeURIComponent(b.name)}/excluded`,
        excluded ? { excluded, hideLive: hideLiveProducts } : { excluded },
      );
      notify(
        excluded
          ? `"${b.name}" excluded — ${plural(data?.withdrawn ?? 0, 'pending item')} withdrawn${hideLiveProducts ? `, ${plural(data?.hidden ?? 0, 'live product')} hidden` : ''}`
          : `"${b.name}" included — ${plural(data?.restored ?? 0, 'item')} back in review${data?.unhidden ? `, ${plural(data.unhidden, 'live product')} shown again` : ''}`,
        { type: 'success' },
      );
      afterChange();
    } catch (err: any) {
      notify(`Failed to update "${b.name}": ${err.response?.data?.error || err.message}`, { type: 'error' });
    }
  };

  const handleBulk = async () => {
    if (!bulkValue || selected.size === 0) return;
    try {
      const { data } = await authInstance.post(`${base}/bulk`, { vendBrandNames: Array.from(selected), ...targetBody(bulkValue) });
      notify(`${plural(data?.saved ?? selected.size, 'brand')} mapped to ${targetLabel(bulkValue)}`, { type: 'success' });
      setSelected(new Set());
      setBulkOpen(false);
      setBulkValue('');
      afterChange();
    } catch (err: any) {
      notify(`Bulk mapping failed: ${err.response?.data?.error || err.message}`, { type: 'error' });
    }
  };

  const toggleSelect = (name: string) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(name)) next.delete(name); else next.add(name);
    return next;
  });

  /** The target dropdown: No brand first, then every Deeliver brand. */
  const targetOptions = [
    <MenuItem key={NO_BRAND} value={NO_BRAND}><em>No brand (leave blank)</em></MenuItem>,
    ...deeliverBrands.map((d) => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>),
  ];

  const chipSx = (c: string, extra: Record<string, unknown> = {}) => ({
    bgcolor: alpha(c, 0.1), color: c, fontWeight: 600, fontSize: '0.75rem', ...extra,
  });

  return (
    <Fade in timeout={300}>
      <Box>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 6, md: 3 }}>
            <GlowCard label="Vend Brands" value={stats?.total ?? 0} color={colors.info} icon={<BrandIcon />} subtitle="Seen on staged items" />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <GlowCard label="Mapped" value={stats?.mapped ?? 0} color={colors.success} icon={<MappedIcon />} subtitle="To a Deeliver brand or No brand" />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <GlowCard
              label="Unmapped"
              value={stats?.unmapped ?? 0}
              color={colors.warning}
              icon={<UnmappedIcon />}
              subtitle={stats?.excluded ? `Kept as in Vend · ${stats.excluded} excluded` : 'Kept as in Vend'}
            />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <GlowCard
              label="No Vend Brand"
              value={stats?.itemsWithoutBrand ?? 0}
              color={colors.purple}
              icon={<NoVendBrandIcon />}
              subtitle="Staged items Vend sends without a brand"
            />
          </Grid>
        </Grid>

        <Paper ref={listTopRef} sx={{
          p: 2, mb: 2.5, bgcolor: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: 2,
          display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
        }}>
          <TextField
            size="small"
            placeholder="Search brands..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            sx={{ ...elevatedInputStyle, minWidth: 240 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><SearchIcon sx={{ color: colors.textMuted, fontSize: 18 }} /></InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 140, ...elevatedInputStyle }}>
            <InputLabel id="brand-mappings-filter" sx={{ color: colors.textMuted }}>Filter</InputLabel>
            <Select
              labelId="brand-mappings-filter"
              value={filterMode}
              label="Filter"
              onChange={(e) => { setFilterMode(e.target.value as typeof filterMode); setPage(1); }}
              sx={{ color: colors.textPrimary }}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="mapped">Mapped</MenuItem>
              <MenuItem value="unmapped">Unmapped</MenuItem>
              <MenuItem value="excluded">Excluded</MenuItem>
            </Select>
          </FormControl>
          <Box sx={{ flex: 1 }} />
          <Button
            size="small"
            variant="outlined"
            startIcon={<SelectAllIcon />}
            disabled={selected.size === 0}
            onClick={() => setBulkOpen(true)}
            sx={{ color: colors.info, borderColor: alpha(colors.info, 0.3), '&:hover': { borderColor: colors.info, bgcolor: alpha(colors.info, 0.08) } }}
          >
            Bulk Assign ({selected.size})
          </Button>
        </Paper>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress sx={{ color: colors.info }} /></Box>
        ) : brands.length === 0 ? (
          <Alert severity="info" sx={{ bgcolor: alpha(colors.info, 0.08), color: colors.info, border: `1px solid ${alpha(colors.info, 0.2)}` }}>
            No brands found. They are read from the staged items of each Vend sync.
          </Alert>
        ) : (
          <>
            <Typography variant="subtitle2" sx={{ color: colors.textMuted, fontWeight: 600, mb: 1 }}>
              Vend brand → Deeliver brand
            </Typography>
            <TableContainer component={Paper} sx={{ bgcolor: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ '& th': { color: colors.textMuted, fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: `1px solid ${colors.border}` } }}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selected.size === brands.length && brands.length > 0}
                        indeterminate={selected.size > 0 && selected.size < brands.length}
                        onChange={() => setSelected(selected.size === brands.length ? new Set() : new Set(brands.map((b) => b.name)))}
                        inputProps={{ 'aria-label': 'Select all brands' }}
                      />
                    </TableCell>
                    <TableCell>Vend brand</TableCell>
                    <TableCell align="center">Items</TableCell>
                    <TableCell align="center">Live</TableCell>
                    <TableCell align="center">In review</TableCell>
                    <TableCell sx={{ minWidth: 240 }}>Deeliver brand</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {brands.map((b) => {
                    const isEditing = editing === b.name;
                    const excludeButton = (
                      <Tooltip title="Exclude: withdraw its pending new items; new ones arrive withdrawn">
                        <IconButton
                          size="small"
                          aria-label={`Exclude ${b.name}`}
                          onClick={() => {
                            if (b.liveCount > 0) {
                              setHideLive(false);
                              setConfirmExclude(b);
                            } else {
                              handleSetExcluded(b, true);
                            }
                          }}
                          sx={{ color: colors.textMuted }}
                        >
                          <ExcludeIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    );
                    const mappingChip = b.noBrand ? (
                      <Chip label="No brand" title="Its products' brand stays blank" size="small" onClick={() => startEdit(b)} sx={chipSx(colors.purple, { cursor: 'pointer' })} />
                    ) : b.mappedTo ? (
                      <Chip label={b.mappedTo.deeliverBrandName} size="small" onClick={() => startEdit(b)} sx={chipSx(colors.success, { cursor: 'pointer' })} />
                    ) : (
                      <Chip
                        label="Unmapped"
                        title="Products keep the Vend brand name as it is"
                        size="small"
                        onClick={() => startEdit(b)}
                        sx={chipSx(colors.warning, { cursor: 'pointer', border: `1px dashed ${alpha(colors.warning, 0.3)}` })}
                      />
                    );
                    return (
                      <TableRow
                        key={b.name}
                        data-excluded={b.excluded ? 'true' : undefined}
                        sx={{
                          ...(b.excluded && !isEditing ? { opacity: 0.55 } : {}),
                          '& td': { color: colors.textSecondary, borderBottom: `1px solid ${alpha(colors.border, 0.5)}`, fontSize: '0.8rem' },
                        }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox checked={selected.has(b.name)} onChange={() => toggleSelect(b.name)} inputProps={{ 'aria-label': `Select ${b.name}` }} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ color: colors.textPrimary, fontWeight: 600, fontSize: '0.8rem' }}>{b.name}</Typography>
                        </TableCell>
                        <TableCell align="center">{b.itemCount}</TableCell>
                        <TableCell align="center">{b.liveCount}</TableCell>
                        <TableCell align="center">{b.waitingCount}</TableCell>
                        <TableCell>
                          {isEditing ? (
                            <FormControl size="small" fullWidth sx={elevatedInputStyle}>
                              <Select
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                displayEmpty
                                inputProps={{ 'aria-label': `Deeliver brand for ${b.name}` }}
                                sx={{ color: colors.textPrimary, fontSize: '0.8rem' }}
                              >
                                <MenuItem value=""><em>-- Select brand --</em></MenuItem>
                                {targetOptions}
                              </Select>
                            </FormControl>
                          ) : b.excluded ? (
                            <Stack direction="row" spacing={0.75} alignItems="center">
                              <Chip
                                label={b.hideLive ? 'Excluded · live hidden' : 'Excluded'}
                                title={b.hideLive
                                  ? 'New items arrive withdrawn and its live products are hidden from the app; Include brings both back'
                                  : 'New items of this brand arrive withdrawn; Include brings them back for review'}
                                size="small"
                                sx={chipSx(colors.textMuted)}
                              />
                              {isMapped(b) && (
                                <Typography variant="caption" sx={{ color: colors.textMuted }}>
                                  → {b.noBrand ? 'No brand' : b.mappedTo?.deeliverBrandName}
                                </Typography>
                              )}
                            </Stack>
                          ) : mappingChip}
                        </TableCell>
                        <TableCell align="center">
                          <Stack direction="row" spacing={0.5} justifyContent="center">
                            {isEditing ? (
                              <>
                                <Tooltip title="Save">
                                  <span>
                                    <IconButton size="small" aria-label={`Save ${b.name}`} onClick={() => handleSave(b)} disabled={!editValue || saving} sx={{ color: colors.success }}>
                                      {saving ? <CircularProgress size={16} /> : <SaveIcon fontSize="small" />}
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <Tooltip title="Cancel">
                                  <IconButton size="small" aria-label="Cancel" onClick={() => setEditing(null)} sx={{ color: colors.textMuted }}>
                                    <CancelIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </>
                            ) : b.excluded ? (
                              <Tooltip title="Include: bring back its withdrawn items for review">
                                <IconButton size="small" aria-label={`Include ${b.name}`} onClick={() => handleSetExcluded(b, false)} sx={{ color: colors.info }}>
                                  <IncludeIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            ) : (
                              <>
                                <Tooltip title={isMapped(b) ? 'Edit mapping' : 'Map this brand'}>
                                  <IconButton size="small" aria-label={`Map ${b.name}`} onClick={() => startEdit(b)} sx={{ color: isMapped(b) ? colors.info : colors.warning }}>
                                    <BrandIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                {isMapped(b) && (
                                  <Tooltip title="Remove mapping (products go back to the Vend name)">
                                    <IconButton size="small" aria-label={`Remove mapping ${b.name}`} onClick={() => handleDelete(b)} sx={{ color: colors.error }}>
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                {excludeButton}
                              </>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            {total > PAGE_SIZE && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Pagination count={Math.ceil(total / PAGE_SIZE)} page={page} onChange={(_, v) => setPage(v)} />
              </Box>
            )}
          </>
        )}

        <Paper sx={{ mt: 2, p: 2, bgcolor: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: 2 }}>
          <Typography variant="body2" sx={{ color: colors.textSecondary }}>
            An unmapped brand keeps its Vend name. Saving a mapping also updates the brand&apos;s live products straight
            away (a brand someone typed onto a product by hand stays), and new products get it when approved.
            &ldquo;No brand&rdquo; leaves the brand blank, for fillers like Miscellaneous.
          </Typography>
        </Paper>

        {/* Bulk Assign */}
        <Dialog open={bulkOpen} onClose={() => setBulkOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { bgcolor: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: 3 } }}>
          <DialogTitle sx={{ color: colors.textPrimary, borderBottom: `1px solid ${colors.border}` }}>Bulk Assign Brands</DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 2 }}>
              Map {plural(selected.size, 'selected Vend brand')} to the same Deeliver brand, or to No brand.
            </Typography>
            <Box sx={{ mb: 2 }}>
              {Array.from(selected).map((name) => (
                <Chip key={name} label={name} size="small" onDelete={() => toggleSelect(name)} sx={{ m: 0.5 }} />
              ))}
            </Box>
            <FormControl fullWidth sx={elevatedInputStyle}>
              <InputLabel id="brand-mappings-bulk" sx={{ color: colors.textMuted }}>Deeliver brand</InputLabel>
              <Select labelId="brand-mappings-bulk" value={bulkValue} label="Deeliver brand" onChange={(e) => setBulkValue(e.target.value)} sx={{ color: colors.textPrimary }}>
                {targetOptions}
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, borderTop: `1px solid ${colors.border}` }}>
            <Button onClick={() => setBulkOpen(false)} sx={{ color: colors.textSecondary }}>Cancel</Button>
            <Button variant="contained" onClick={handleBulk} disabled={!bulkValue}>Assign</Button>
          </DialogActions>
        </Dialog>

        {/* Confirm Exclude (a brand with live products) */}
        <Dialog
          open={confirmExclude !== null}
          onClose={() => setConfirmExclude(null)}
          maxWidth="xs"
          fullWidth
          PaperProps={{ sx: { bgcolor: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: 3 } }}
        >
          <DialogTitle sx={{ color: colors.textPrimary, borderBottom: `1px solid ${colors.border}` }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <ExcludeIcon sx={{ color: colors.warning }} />
              <span>Exclude &ldquo;{confirmExclude?.name}&rdquo;?</span>
            </Stack>
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ color: colors.textSecondary }}>
              Excluding it withdraws its pending new items, and new ones arrive withdrawn. Its mapping is kept.
              Including it later brings the withdrawn items back.
            </Typography>
            <FormControlLabel
              sx={{ mt: 2, alignItems: 'flex-start' }}
              control={<Checkbox checked={hideLive} onChange={(e) => setHideLive(e.target.checked)} sx={{ pt: 0.25 }} />}
              label={(
                <Typography variant="body2" sx={{ color: colors.textPrimary }}>
                  Also hide its {plural(confirmExclude?.liveCount ?? 0, 'live product')} from the app
                </Typography>
              )}
            />
            <Typography variant="caption" component="p" sx={{ color: colors.textMuted, ml: 4 }}>
              {hideLive
                ? 'They stay in Vend and keep syncing, just not shown in the app. Including the brand shows them again.'
                : 'Left unticked, products that already exist stay in the app and keep syncing.'}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, borderTop: `1px solid ${colors.border}` }}>
            <Button onClick={() => setConfirmExclude(null)} sx={{ color: colors.textSecondary }}>Cancel</Button>
            <Button
              variant="contained"
              onClick={() => {
                const b = confirmExclude;
                setConfirmExclude(null);
                if (b) handleSetExcluded(b, true, hideLive);
              }}
              sx={{ bgcolor: colors.warning, color: '#000', fontWeight: 700, '&:hover': { bgcolor: colors.warning } }}
            >
              Exclude
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Fade>
  );
}
